import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuth from '../auth/useAuth';
import { getOidcConfig, oidcLoginUrl } from '../api';
import BrandLogo from '../components/BrandLogo';
import Button from '../components/ui/Button';
import { MvLandscape } from '../components/brand/MvLandscape';

function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sso, setSso] = useState(null);

  // Single sign-on is offered only when the server has it configured.
  useEffect(() => {
    let cancelled = false;

    getOidcConfig()
      .then((config) => {
        if (!cancelled && config?.enabled) {
          setSso(config);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  // Coming back from the provider without a session: say why.
  useEffect(() => {
    const code = new URLSearchParams(location.search).get('sso_error');

    if (code) {
      setError(
        t(`login.ssoError.${code}`, {
          defaultValue: t('login.ssoError.failed'),
        }),
      );
    }
    // Only the URL the page was opened with matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSso() {
    const from = location.state?.from;
    const returnTo = from
      ? `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`
      : '/';

    setIsSubmitting(true);
    window.location.assign(oidcLoginUrl(returnTo));
  }

  useEffect(() => {
    const rememberedEmail = localStorage.getItem(
      'remembered_email',
    );

    if (rememberedEmail) {
      setEmail(rememberedEmail);
    }

    try {
      if (sessionStorage.getItem('disiSessionExpired')) {
        sessionStorage.removeItem('disiSessionExpired');
        setError(t('login.sessionExpired'));
      }
    } catch {
      // Storage unavailable - just skip the explanatory banner.
    }
    // Only ever read on first mount; `t` identity churn shouldn't re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError(t('login.errorEnterEmail'));
      return;
    }

    if (!password) {
      setError(t('login.errorEnterPassword'));
      return;
    }

    if (password.length < 6) {
      setError(t('login.errorPasswordLength'));
      return;
    }

    if (needsTwoFactor && otp.trim().length !== 6) {
      setError(t('login.errorEnterTwoFactor'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      await login({
        login: normalizedEmail,
        password,
        rememberMe,
        otp: needsTwoFactor ? otp.trim() : undefined,
        rememberDevice: needsTwoFactor && rememberDevice,
      });

      if (rememberMe) {
        localStorage.setItem(
          'remembered_email',
          normalizedEmail,
        );
      } else {
        localStorage.removeItem(
          'remembered_email',
        );
      }

      const from = location.state?.from;

      // "My publications" lists articles and news, so either right opens it.
      const canAccessMyArticles =
        ['articles.manage', 'news.manage'].some((right) =>
          user?.rightsAnywhere?.includes(right),
        );

      const isMyArticlesRoute =
        from?.pathname === '/my-articles';

      const shouldRestoreRoute =
        from &&
        (!isMyArticlesRoute || canAccessMyArticles);

      navigate(
        shouldRestoreRoute
          ? {
              pathname: from.pathname,
              search: from.search,
              hash: from.hash,
            }
          : '/',
        {
          replace: true,
        },
      );

    } catch (error) {
      console.error(error);

      if (
        !needsTwoFactor &&
        error.data?.message === 'TOTP_REQUIRED'
      ) {
        setNeedsTwoFactor(true);
        setError('');
        return;
      }

      if (error.data?.message === 'TOO_MANY_ATTEMPTS') {
        setError(
          t('login.errorTooManyAttempts', {
            count: error.data.retryAfterMinutes ?? 15,
          }),
        );
        return;
      }

      setError(
        error.message ||
          t('login.errorGeneric'),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <section
        className="login-card"
        aria-labelledby="login-title"
      >
        <div className="login-brand">
          <Link
            to="/"
            className="login-logo"
            aria-label={t('login.logoAriaLabel')}
          >
            <BrandLogo variant="mark" />
          </Link>

          <div>
            <p className="login-eyebrow">
              {t('login.brandEyebrow')}
            </p>

            <span>{t('login.brandName')}</span>
          </div>
        </div>

        <div className="login-heading">
          <h1 id="login-title">
            {needsTwoFactor
              ? t('login.twoFactorTitle')
              : t('login.welcomeBack')}
          </h1>

          <p>
            {needsTwoFactor
              ? t('login.twoFactorSubtitle')
              : t('login.subtitle')}
          </p>
        </div>

        {sso && !needsTwoFactor && (
          <div className="login-sso">
            <button
              className="login-submit login-sso-button"
              type="button"
              disabled={isSubmitting}
              onClick={handleSso}
            >
              <i className="fa-solid fa-key" />
              {sso.label || t('login.ssoButton')}
            </button>

            <p className="login-sso-divider">
              <span>{t('login.ssoDivider')}</span>
            </p>
          </div>
        )}

        <form
          className="login-form"
          onSubmit={handleSubmit}
          noValidate
        >
          {error && (
            <div
              className="login-message login-message-error"
              role="alert"
            >
              <i className="fa-solid fa-circle-exclamation" />

              <span>{error}</span>
            </div>
          )}

          {needsTwoFactor ? (
            <div className="login-field">
              <label htmlFor="login-otp">
                {t('login.twoFactorLabel')}
              </label>

              <div className="login-input-wrapper">
                <i className="fa-solid fa-shield-halved" />

                <input
                  id="login-otp"
                  name="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder={t('login.twoFactorPlaceholder')}
                  maxLength={6}
                  value={otp}
                  disabled={isSubmitting}
                  autoFocus
                  onChange={(event) => {
                    setOtp(
                      event.target.value.replace(/\D/g, ''),
                    );
                    setError('');
                  }}
                />
              </div>

              <label className="login-remember">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  disabled={isSubmitting}
                  onChange={(event) => {
                    setRememberDevice(event.target.checked);
                  }}
                />

                <span>{t('login.rememberDevice')}</span>
              </label>
            </div>
          ) : (
            <>
              <div className="login-field">
                <label htmlFor="login-email">
                  {t('login.emailLabel')}
                </label>

                <div className="login-input-wrapper">
                  <i className="fa-solid fa-envelope" />

                  <input
                    id="login-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t('login.emailPlaceholder')}
                    value={email}
                    disabled={isSubmitting}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setError('');
                    }}
                  />
                </div>
              </div>

              <div className="login-field">
                <div className="login-field-header">
                  <label htmlFor="login-password">
                    {t('login.passwordLabel')}
                  </label>

                  <Button
                    variant="text"
                    disabled={isSubmitting}
                    onClick={() => {
                      window.alert(
                        t('login.forgotPasswordAlert'),
                      );
                    }}
                  >
                    {t('login.forgotPassword')}
                  </Button>
                </div>

                <div className="login-input-wrapper">
                  <i className="fa-solid fa-lock" />

                  <input
                    id="login-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder={t('login.passwordPlaceholder')}
                    value={password}
                    disabled={isSubmitting}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setError('');
                    }}
                  />

                  <button
                    className="login-password-toggle"
                    type="button"
                    aria-label={
                      showPassword
                        ? t('login.hidePassword')
                        : t('login.showPassword')
                    }
                    aria-pressed={showPassword}
                    disabled={isSubmitting}
                    onClick={() => {
                      setShowPassword((current) => !current);
                    }}
                  >
                    <i
                      className={
                        showPassword
                          ? 'fa-solid fa-eye-slash'
                          : 'fa-solid fa-eye'
                      }
                    />
                  </button>
                </div>
              </div>

              <label className="login-remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  disabled={isSubmitting}
                  onChange={(event) => {
                    setRememberMe(event.target.checked);
                  }}
                />

                <span>{t('login.rememberMe')}</span>
              </label>
            </>
          )}

          {needsTwoFactor && (
            <Button
              variant="text"
              disabled={isSubmitting}
              onClick={() => {
                setNeedsTwoFactor(false);
                setOtp('');
                setError('');
              }}
            >
              {t('login.backButton')}
            </Button>
          )}

          <button
            className="login-submit"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner login-spinner" />
                {needsTwoFactor
                  ? t('login.verifying')
                  : t('login.signingIn')}
              </>
            ) : (
              <>
                {needsTwoFactor
                  ? t('login.verify')
                  : t('login.signIn')}
                <i className="fa-solid fa-arrow-right" />
              </>
            )}
          </button>
        </form>

        <div className="login-demo-note">
          <i className="fa-solid fa-flask" />

          <p>
            <strong>{t('login.demoNoteStrong')}</strong> {t('login.demoNoteText')}
          </p>
        </div>

        <p className="login-footer">
          {t('login.footer')}
        </p>
      </section>

      <aside className="login-visual" aria-hidden="true">
        <MvLandscape
          className="login-landscape"
          bands={[
            { color: 'var(--mv-himmelblau)', top: 22, amplitude: 34, flip: true },
            { color: 'var(--mv-wiesengruen)', top: 52, amplitude: 28 },
            { color: 'var(--mv-lichtgruen)', top: 78, amplitude: 18, flip: true },
          ]}
        />

        <div className="login-visual-icon">
          <i className="fa-solid fa-shield-halved" />
        </div>

        <div className="login-visual-copy">
          <p className="login-eyebrow">
            {t('login.visualEyebrow')}
          </p>

          <h2>
            {t('login.visualTitle')}
          </h2>

          <p>
            {t('login.visualDescription')}
          </p>
        </div>

        <div className="login-feature-list">
          <div>
            <i className="fa-solid fa-user-shield" />
            <span>{t('login.featureRoleBased')}</span>
          </div>

          <div>
            <i className="fa-solid fa-book-open" />
            <span>{t('login.featureDocs')}</span>
          </div>

          <div>
            <i className="fa-solid fa-wand-magic-sparkles" />
            <span>{t('login.featureAi')}</span>
          </div>
        </div>
      </aside>
    </main>
  );
}

export default Login;
